import { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import Header from "../components/app/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { INTAKE_SECTIONS, fieldVisible, validateSection, requiredFieldStats, sectionStats } from "../lib/intakeSchema";
import { Check, ChevronLeft, ChevronRight, Lock, Save, CheckCircle2, Eye } from "lucide-react";
import DigestPreviewModal from "../components/app/DigestPreviewModal";

const NAVY = "#010082";

function PillChoice({ selected, onClick, children, dataTestId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={dataTestId}
      aria-pressed={selected}
      className={`inline-flex items-center justify-center gap-1.5 h-10 px-4 rounded-full text-xs font-semibold border transition-all ${
        selected
          ? "text-white border-transparent shadow-sm"
          : "bg-white text-stone-700 border-stone-300 hover:border-stone-500 hover:bg-stone-50"
      }`}
      style={selected ? { backgroundColor: NAVY } : undefined}
    >
      {selected && <Check className="h-3.5 w-3.5" />}
      {children}
    </button>
  );
}

function FieldLabel({ field, error }) {
  return (
    <div className="flex items-baseline gap-2 flex-wrap">
      <Label className="text-sm font-semibold text-stone-800">
        {field.label}
        {field.required && <span className="text-rose-600 ml-0.5">*</span>}
      </Label>
      {field.sensitive && (
        <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-amber-700">
          <Lock className="h-3 w-3" /> Private
        </span>
      )}
      {field.hint && <span className="text-[11px] text-stone-500 italic">{field.hint}</span>}
      {error && <span className="text-[11px] text-rose-600 ml-auto">{error}</span>}
    </div>
  );
}

function RenderField({ field, value, values, onChange, error }) {
  if (!fieldVisible(field, values)) return null;

  if (field.type === "text" || field.type === "email" || field.type === "tel" || field.type === "number") {
    return (
      <div>
        <FieldLabel field={field} error={error} />
        <Input
          type={field.type === "number" ? "number" : field.type === "email" ? "email" : "text"}
          value={value ?? ""}
          placeholder={field.placeholder || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`intake-${field.key}`}
          className="mt-1.5 h-10 bg-white border-stone-300"
        />
      </div>
    );
  }
  if (field.type === "date") {
    return (
      <div>
        <FieldLabel field={field} error={error} />
        <Input
          type="date"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`intake-${field.key}`}
          className="mt-1.5 h-10 bg-white border-stone-300"
        />
      </div>
    );
  }
  if (field.type === "textarea") {
    return (
      <div>
        <FieldLabel field={field} error={error} />
        <Textarea
          value={value ?? ""}
          placeholder={field.placeholder || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`intake-${field.key}`}
          rows={3}
          className="mt-1.5 bg-white border-stone-300 leading-relaxed"
        />
      </div>
    );
  }
  if (field.type === "radio") {
    return (
      <div>
        <FieldLabel field={field} error={error} />
        <div className="mt-2 flex flex-wrap gap-2">
          {field.options.map((opt) => (
            <PillChoice
              key={opt}
              selected={value === opt}
              onClick={() => onChange(opt)}
              dataTestId={`intake-${field.key}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              {opt}
            </PillChoice>
          ))}
        </div>
      </div>
    );
  }
  if (field.type === "multi") {
    const arr = Array.isArray(value) ? value : [];
    return (
      <div>
        <FieldLabel field={field} error={error} />
        <div className="mt-2 flex flex-wrap gap-2">
          {field.options.map((opt) => {
            const on = arr.includes(opt);
            return (
              <PillChoice
                key={opt}
                selected={on}
                onClick={() => onChange(on ? arr.filter((x) => x !== opt) : [...arr, opt])}
                dataTestId={`intake-${field.key}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                {opt}
              </PillChoice>
            );
          })}
        </div>
      </div>
    );
  }
  if (field.type === "appliance_multi") {
    const current = value && typeof value === "object" && !Array.isArray(value) ? value : {};
    return (
      <div>
        <FieldLabel field={field} error={error} />
        <div className="mt-2 space-y-2">
          {field.options.map((opt) => {
            const row = current[opt.key] || { included: false, brand: "", model: "" };
            const on = !!row.included;
            const patch = (next) => onChange({ ...current, [opt.key]: { ...row, ...next } });
            return (
              <div
                key={opt.key}
                className={`rounded-md border transition-colors ${on ? "border-[#010082]/30 bg-[#010082]/[0.035]" : "border-stone-200 bg-white"}`}
              >
                <div className="flex items-center flex-wrap gap-3 px-3.5 py-2.5">
                  <PillChoice
                    selected={on}
                    onClick={() => patch({ included: !on })}
                    dataTestId={`intake-appliance-${opt.key}`}
                  >
                    {opt.label}
                  </PillChoice>
                  {on && (
                    <>
                      <Input
                        value={row.brand || ""}
                        placeholder="Brand"
                        onChange={(e) => patch({ brand: e.target.value })}
                        data-testid={`intake-appliance-${opt.key}-brand`}
                        className="h-9 w-[180px] bg-white border-stone-300 text-sm"
                      />
                      <Input
                        value={row.model || ""}
                        placeholder="Model"
                        onChange={(e) => patch({ model: e.target.value })}
                        data-testid={`intake-appliance-${opt.key}-model`}
                        className="h-9 w-[200px] bg-white border-stone-300 text-sm"
                      />
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
  if (field.type === "consent") {
    return (
      <label
        className={`flex items-start gap-3 rounded-md border p-4 cursor-pointer transition-colors ${value ? "border-[#010082]/30 bg-[#010082]/[0.035]" : "border-stone-300 bg-white"}`}
      >
        <input
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          data-testid={`intake-${field.key}`}
          className="mt-1 h-4 w-4 accent-[#010082]"
        />
        <span className="text-sm text-stone-700 leading-relaxed">
          {field.label} {field.required && <span className="text-rose-600">*</span>}
        </span>
        {error && <span className="text-[11px] text-rose-600 ml-auto shrink-0">{error}</span>}
      </label>
    );
  }
  return null;
}

export default function IntakeForm() {
  const nav = useNavigate();
  const { id } = useParams();
  const editing = !!id;
  const [listing, setListing] = useState(null);
  const [stepIdx, setStepIdx] = useState(0);
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [intakeStatus, setIntakeStatus] = useState("draft");
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    if (!editing) return;
    (async () => {
      try {
        const { data } = await api.get(`/listings/${id}`);
        setListing(data);
        setValues(data.intake || {});
        setLastSavedAt(data.intake_last_saved_at || data.intake_submitted_at || null);
        setIntakeStatus(data.intake_status || (data.intake_submitted_at ? "complete" : "draft"));
      } catch (e) {
        toast.error("Could not load this listing.");
        nav("/app");
      }
    })();
  }, [editing, id, nav]);

  const section = INTAKE_SECTIONS[stepIdx];
  const isLast = stepIdx === INTAKE_SECTIONS.length - 1;

  const setField = (key, v) => {
    setValues((prev) => ({ ...prev, [key]: v }));
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const n = { ...prev }; delete n[key]; return n;
    });
  };

  const sectionsStatus = useMemo(() => {
    return INTAKE_SECTIONS.map((s) => {
      const stats = sectionStats(s, values);
      const touched = s.fields.some((f) => fieldVisible(f, values) && values[f.key] !== undefined && values[f.key] !== "" && !(Array.isArray(values[f.key]) && values[f.key].length === 0));
      return { ...stats, ok: stats.complete, touched };
    });
  }, [values]);

  const progress = useMemo(() => requiredFieldStats(values), [values]);

  const buildTopLevel = () => ({
    property_address: values.property_address || values.city_state_zip || "",
    seller_names: values.seller_name || "",
    seller_email: values.email || "",
    target_list_date: values.ideal_list_date || null,
    closing_date: values.ideal_closing_date || null,
    secondary_seller_email: values.secondary_seller_email || "",
    tc_name: values.tc_name || "",
    tc_email: values.tc_email || "",
    daily_digest_enabled: values.daily_digest_enabled !== false,
    daily_digest_recipients: Array.isArray(values.daily_digest_recipients) ? values.daily_digest_recipients : ["Agent", "Seller", "TC"],
    intake: values,
  });

  // Debounced autosave on blur (only for existing listings — new drafts must
  // click "Save Draft" once first so we don't create stray listings on every blur).
  const autosaveTimer = useRef(null);
  const [autosaveState, setAutosaveState] = useState("idle"); // "idle" | "saving" | "saved"
  const scheduleAutosave = () => {
    if (!editing) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(async () => {
      setAutosaveState("saving");
      try {
        const now = new Date().toISOString();
        const payload = { ...buildTopLevel() };
        if (intakeStatus !== "complete") {
          payload.intake_status = "draft";
        }
        payload.intake_last_saved_at = now;
        const { data } = await api.put(`/listings/${id}`, payload);
        setLastSavedAt(data.intake_last_saved_at || now);
        setAutosaveState("saved");
        setTimeout(() => setAutosaveState("idle"), 2500);
      } catch (_) {
        setAutosaveState("idle");
      }
    }, 900);
  };

  const saveDraft = async () => {
    setSavingDraft(true);
    try {
      const now = new Date().toISOString();
      const payload = { ...buildTopLevel(), intake_status: "draft", intake_last_saved_at: now };
      if (editing) {
        const { data } = await api.put(`/listings/${id}`, payload);
        setLastSavedAt(data.intake_last_saved_at || now);
        setIntakeStatus(data.intake_status || "draft");
        toast.success("Draft saved");
      } else {
        // First draft of a new listing — create the listing in Pre-Listing / draft state
        const { data } = await api.post(`/listings`, { ...payload, listing_status: "Pre-Listing" });
        toast.success("Draft saved — you can leave and come back any time.");
        nav(`/listing/${data.id}/intake`, { replace: true });
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save draft.");
    } finally { setSavingDraft(false); }
  };

  const goNext = async () => {
    const sectionErrs = validateSection(section, values);
    if (Object.keys(sectionErrs).length) {
      setErrors(sectionErrs);
      toast.error("Please complete the required fields.");
      return;
    }
    setErrors({});
    if (!isLast) { setStepIdx(stepIdx + 1); window.scrollTo({ top: 0, behavior: "smooth" }); return; }

    // Final submit — validate all sections one more time
    let allErrs = {};
    for (const s of INTAKE_SECTIONS) Object.assign(allErrs, validateSection(s, values));
    if (Object.keys(allErrs).length) {
      setErrors(allErrs);
      const firstBadIdx = INTAKE_SECTIONS.findIndex((s) => Object.keys(validateSection(s, values)).length > 0);
      if (firstBadIdx >= 0) setStepIdx(firstBadIdx);
      toast.error("Please complete the required fields.");
      return;
    }

    setBusy(true);
    try {
      const now = new Date().toISOString();
      const topLevel = buildTopLevel();
      if (editing) {
        const { data } = await api.put(`/listings/${id}`, {
          ...topLevel,
          intake_submitted_at: now,
          intake_status: "complete",
          intake_last_saved_at: now,
        });
        toast.success("Intake complete.");
        nav(`/listing/${data.id}`);
      } else {
        const { data } = await api.post(`/listings`, {
          ...topLevel,
          listing_status: "Pre-Listing",
          intake_submitted_at: now,
          intake_status: "complete",
          intake_last_saved_at: now,
        });
        toast.success("Listing created. Opening the 7-phase workflow.");
        nav(`/listing/${data.id}`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save intake.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#faf7f2]">
      <Header />
      <main className="max-w-6xl mx-auto px-6 pt-10 pb-20" data-testid="intake-form-page">
        <div className="mb-10">
          <p className="overline text-stone-500">
            {editing ? "Edit Seller Intake" : "New Listing · Seller Intake"}
          </p>
          <h1 className="font-heading font-semibold tracking-tight text-stone-900 text-4xl sm:text-5xl mt-2">
            Every detail we need <em className="font-heading italic font-normal">to sell this home</em> with confidence.
          </h1>
          <p className="text-sm text-stone-600 mt-3 max-w-2xl leading-relaxed">
            Nine short sections. The <strong>Ideal List Date</strong> you capture in Section 02 anchors every deadline across the 7-phase workflow — change it later from Edit Details and all offsets re-calculate.
          </p>

          <div className="mt-5 flex items-center flex-wrap gap-3" data-testid="intake-progress-strip">
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-widest border ${
                intakeStatus === "complete"
                  ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                  : "bg-amber-50 border-amber-200 text-amber-800"
              }`}
              data-testid="intake-status-chip"
            >
              {intakeStatus === "complete" ? (
                <>
                  <CheckCircle2 className="h-3 w-3" />
                  Complete
                </>
              ) : (
                <>Draft · In Progress</>
              )}
            </span>
            <span className="text-xs text-stone-600 tabular-nums" data-testid="intake-progress-count">
              <span className="font-semibold text-stone-900">{progress.filled}</span>
              <span className="text-stone-400"> / </span>
              <span>{progress.total}</span>
              <span className="text-stone-500 ml-1">required fields complete</span>
            </span>
            <div className="h-1.5 w-32 bg-stone-200 rounded-full overflow-hidden" data-testid="intake-progress-bar">
              <div
                className="h-full transition-all duration-300"
                style={{ width: `${progress.pct}%`, backgroundColor: "#010082" }}
              />
            </div>
            {lastSavedAt && (
              <span className="text-[11px] text-stone-500" data-testid="intake-last-saved">
                Last saved {new Date(lastSavedAt).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
              </span>
            )}
            {autosaveState !== "idle" && (
              <span
                className={`text-[11px] font-semibold uppercase tracking-widest flex items-center gap-1 ${autosaveState === "saved" ? "text-emerald-700" : "text-stone-400"}`}
                data-testid="intake-autosave-indicator"
              >
                {autosaveState === "saving" ? "Saving…" : (<><Check className="h-3 w-3" /> Saved</>)}
              </span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <aside className="lg:col-span-3">
            <ol className="space-y-1.5 sticky top-6" data-testid="intake-section-nav">
              {INTAKE_SECTIONS.map((s, i) => {
                const active = i === stepIdx;
                const status = sectionsStatus[i] || {};
                const done = status.ok;
                const hasRequired = status.hasRequired;
                const remaining = status.remaining || 0;
                return (
                  <li key={s.id}>
                    <button
                      type="button"
                      onClick={() => setStepIdx(i)}
                      className={`w-full text-left flex items-start gap-3 px-3.5 py-2.5 rounded-md transition-all ${
                        active ? "bg-[#010082] text-white" : "bg-white border border-stone-200 text-stone-700 hover:border-stone-400"
                      }`}
                      data-testid={`intake-nav-${s.id}`}
                    >
                      <span className={`shrink-0 h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-semibold font-heading ${active ? "bg-white/15 text-white" : done ? "bg-emerald-600 text-white" : "bg-stone-100 text-stone-500"}`}>
                        {done ? <Check className="h-3.5 w-3.5" /> : s.index}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className={`text-[10px] uppercase tracking-widest ${active ? "text-white/70" : "text-stone-400"} font-semibold`}>Section {s.index}</div>
                        <div className={`text-[13px] font-medium leading-snug ${active ? "text-white" : "text-stone-800"}`}>{s.title}</div>
                      </div>
                      {hasRequired && (
                        done ? (
                          <span
                            className={`shrink-0 inline-flex items-center gap-1 h-5 px-1.5 rounded-full text-[9px] font-semibold uppercase tracking-widest ${active ? "bg-white/20 text-white" : "bg-emerald-50 text-emerald-700 border border-emerald-200"}`}
                            data-testid={`intake-rail-badge-${s.id}`}
                            title="All required fields complete"
                          >
                            <CheckCircle2 className="h-3 w-3" />
                          </span>
                        ) : (
                          <span
                            className={`shrink-0 inline-flex items-center h-5 px-1.5 rounded-full text-[9px] font-semibold uppercase tracking-widest tabular-nums ${active ? "bg-white/20 text-white" : "bg-amber-50 text-amber-800 border border-amber-200"}`}
                            data-testid={`intake-rail-badge-${s.id}`}
                            title={`${remaining} required field${remaining === 1 ? "" : "s"} remaining`}
                          >
                            {remaining} left
                          </span>
                        )
                      )}
                    </button>
                  </li>
                );
              })}
            </ol>
          </aside>

          <section className="lg:col-span-9">
            <div className="rounded-md border border-stone-200 bg-white p-8" data-testid={`intake-section-${section.id}`}>
              <p className="section-index text-stone-500 text-sm">Section {section.index}</p>
              <h2 className="font-heading font-semibold tracking-tight text-stone-900 text-3xl mt-1">
                {section.title}
              </h2>
              <p className="text-sm text-stone-500 mt-1 leading-relaxed">{section.subtitle}</p>
              {section.privacyNotice && (
                <p className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-widest text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1">
                  <Lock className="h-3 w-3" /> {section.privacyNotice}
                </p>
              )}

              <div className="hr-serif my-6" />

              <div className="space-y-6" onBlurCapture={scheduleAutosave}>
                {section.fields.map((f) => (
                  <RenderField
                    key={f.key}
                    field={f}
                    value={values[f.key]}
                    values={values}
                    onChange={(v) => setField(f.key, v)}
                    error={errors[f.key]}
                  />
                ))}
              </div>

              <div className="hr-serif my-8" />
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    if (stepIdx === 0) nav(editing ? `/listing/${id}` : "/app");
                    else setStepIdx(stepIdx - 1);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  disabled={busy || savingDraft}
                  className="h-10 rounded-full border-stone-300"
                  data-testid="intake-back"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  {stepIdx === 0 ? "Cancel" : "Back"}
                </Button>
                <div className="flex items-center gap-2">
                  {editing && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setPreviewOpen(true)}
                  className="h-9 rounded-full border-stone-300 text-xs"
                  data-testid="intake-preview-digest"
                >
                  <Eye className="h-3.5 w-3.5 mr-1.5" />
                  Preview digest
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={saveDraft}
                disabled={busy || savingDraft}
                className="h-10 rounded-full border-stone-300"
                data-testid="intake-save-draft"
              >
                <Save className="h-4 w-4 mr-1.5" />
                {savingDraft ? "Saving…" : "Save Draft"}
              </Button>
                  <Button
                    type="button"
                    onClick={goNext}
                    disabled={busy || savingDraft}
                    className="h-10 rounded-full px-6 text-white"
                    style={{ backgroundColor: NAVY }}
                    data-testid="intake-next"
                  >
                    {isLast ? (busy ? "Saving…" : (editing ? "Complete Intake" : "Create Listing")) : "Continue"}
                    {!isLast && <ChevronRight className="h-4 w-4 ml-1" />}
                  </Button>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
      {editing && (
        <DigestPreviewModal
          listingId={id}
          open={previewOpen}
          onOpenChange={setPreviewOpen}
        />
      )}
    </div>
  );
}
